import * as React from "react";
import { createTranslator, isLocale, type Locale, type Translator } from "@mandrac/shared";

const KEY = "mandrac.locale";
function initialLocale(): Locale {
  try {
    const s = localStorage.getItem(KEY);
    if (isLocale(s)) return s;
  } catch {
    /* ignore */
  }
  return "hr";
}

interface I18nValue extends Translator {
  setLocale: (l: Locale) => void;
  /** translate an API error/message key with params, falling back to the generic error */
  te: (key: string | undefined, params?: Record<string, string | number>) => string;
}

const I18nContext = React.createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(initialLocale);
  const value = React.useMemo<I18nValue>(() => {
    const tr = createTranslator(locale);
    return {
      ...tr,
      setLocale: (l) => {
        try {
          localStorage.setItem(KEY, l);
        } catch {
          /* ignore */
        }
        setLocaleState(l);
      },
      te: (key, params) => (key && tr.has(key) ? tr.tx(key, params) : tr.t("error.generic")),
    };
  }, [locale]);
  React.useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = React.useContext(I18nContext);
  if (!v) throw new Error("useI18n outside I18nProvider");
  return v;
}
